import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Edit2, Lock, Save, X } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { PasswordChangeModal } from '../../components/auth/PasswordChangeModal';
import { useAuthStore } from '../../store/auth.store';
import { userService } from '../../services/user.service';
import toast from 'react-hot-toast';

const profileSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  email: z.string().optional().refine((val) => !val || val === '' || z.string().email().safeParse(val).success, {
    message: 'Invalid email',
  }),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export const ProfilePage: React.FC = () => {
  const { user: authUser, setUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const { data: userDetails, isLoading } = useQuery({
    queryKey: ['user', authUser?.userId],
    queryFn: () => userService.getById(authUser!.userId),
    enabled: !!authUser?.userId,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    values: userDetails ? {
      fullName: userDetails.fullName,
      email: userDetails.email || '',
    } : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: (data: ProfileFormData) => 
      userService.update(authUser!.userId, {
        fullName: data.fullName,
        email: data.email || undefined,
      }),
    onSuccess: async (updatedUser) => {
      toast.success('Profile updated successfully!');
      await queryClient.invalidateQueries({ queryKey: ['user', authUser?.userId] });
      setIsEditing(false);
      
      // Update auth store with new user data
      if (authUser) {
        setUser({
          ...authUser,
          // Note: backend should return updated user info in login response
        });
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    },
  });

  const onSubmit = (data: ProfileFormData) => {
    updateMutation.mutate(data);
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Loading profile...</p>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">User not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-neutral-charcoal mb-2">Profile</h1>
        <p className="text-gray-600">Manage your account settings and preferences</p>
      </div>

      {/* Profile Information Card */}
      <Card
        title="Profile Information"
        action={
          !isEditing && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              <Edit2 size={16} className="mr-2" />
              Edit Profile
            </Button>
          )
        }
      >
        {isEditing ? (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Full Name"
                placeholder="Enter your full name"
                error={errors.fullName?.message}
                {...register('fullName')}
                required
              />
              <Input
                label="Email"
                type="email"
                placeholder="Enter your email"
                error={errors.email?.message as string}
                {...register('email')}
              />
            </div>

            <div className="flex items-center gap-2 pt-4">
              <Button
                type="submit"
                variant="primary"
                loading={updateMutation.isPending}
              >
                <Save size={16} className="mr-2" />
                Save Changes
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                <X size={16} className="mr-2" />
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4 pb-4 border-b">
              <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center">
                <User size={40} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-neutral-charcoal">
                  {userDetails.fullName}
                </h2>
                <p className="text-gray-500">@{userDetails.username}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-500">Username</label>
                <p className="text-neutral-charcoal mt-1">{userDetails.username}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Email</label>
                <p className="text-neutral-charcoal mt-1">
                  {userDetails.email || 'Not provided'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <div className="mt-1">
                  <Badge variant={userDetails.active ? 'success' : 'error'}>
                    {userDetails.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Roles</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {userDetails.roles.map((role) => (
                    <Badge key={role} variant="info">
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Security Card */}
      <Card title="Security">
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Password</h3>
            <p className="text-sm text-gray-500 mb-4">
              Keep your account secure by using a strong password
            </p>
            <Button
              variant="secondary"
              onClick={() => setShowPasswordModal(true)}
            >
              <Lock size={16} className="mr-2" />
              Change Password
            </Button>
          </div>

          {userDetails.passwordChangeRequired && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>Action Required:</strong> You must change your password to continue using the system.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Account Details Card */}
      <Card title="Account Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-500">User ID</label>
            <p className="text-neutral-charcoal mt-1">{userDetails.id}</p>
          </div>
          {userDetails.driverId && (
            <div>
              <label className="text-sm font-medium text-gray-500">Driver ID</label>
              <p className="text-neutral-charcoal mt-1">{userDetails.driverId}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-500">Account Created</label>
            <p className="text-neutral-charcoal mt-1">
              {new Date(userDetails.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Last Updated</label>
            <p className="text-neutral-charcoal mt-1">
              {new Date(userDetails.updatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
      </Card>

      {/* Password Change Modal */}
      {authUser && (
        <PasswordChangeModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          userId={authUser.userId}
          isRequired={false}
          onSuccess={() => {
            setShowPasswordModal(false);
            toast.success('Password changed successfully!');
          }}
        />
      )}
    </div>
  );
};
